import { useState, useEffect } from 'react'
import { Loader2, AlertTriangle, Clock, CreditCard, HelpCircle, TrendingUp, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { api } from '../../api/client'

const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0)
const fmtD = (d: string) => d ? new Date(d + 'T00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

interface Txn {
  id: string; date: string; vendor: string; description: string; amount: number;
  category: string; account: string; flags?: string; pending?: boolean;
}
interface OverdueCard { name: string; institution: string; due_date: string; balance: number; days_overdue: number }
interface MonitorResp {
  large: Txn[]; pending: Txn[]; uncategorized: Txn[]; unusual: Txn[]; overdue_cards: OverdueCard[];
  counts: { large: number; pending: number; uncategorized: number; unusual: number; overdue_cards: number };
}

type Section = 'large' | 'unusual' | 'pending' | 'uncategorized' | 'overdue'
type SortCol = 'date' | 'vendor' | 'category' | 'account' | 'amount'

export function MonitorPage() {
  const [data, setData] = useState<MonitorResp | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [active, setActive] = useState<Section>('large')
  const [sortCol, setSortCol] = useState<SortCol>('date')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')

  const toggleSort = (col: SortCol) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortCol(col); setSortDir(col === 'amount' ? 'desc' : 'asc') }
  }

  const sortTxns = (list: Txn[]) => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...list].sort((a, b) => {
      switch (sortCol) {
        case 'date': return dir * a.date.localeCompare(b.date)
        case 'vendor': return dir * a.vendor.localeCompare(b.vendor)
        case 'category': return dir * a.category.localeCompare(b.category)
        case 'account': return dir * a.account.localeCompare(b.account)
        case 'amount': return dir * (Math.abs(a.amount) - Math.abs(b.amount))
        default: return 0
      }
    })
  }

  const SortIcon = ({ col }: { col: string }) => {
    if (sortCol !== col) return <ArrowUpDown className="h-3 w-3 text-slate-600" />
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-indigo-400" /> : <ArrowDown className="h-3 w-3 text-indigo-400" />
  }

  useEffect(() => {
    api.get<MonitorResp>('/api/monitor')
      .then(setData)
      .catch((e: Error) => setError(e.message || 'Failed to load monitor data'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>

  const c = data?.counts ?? { large: 0, pending: 0, uncategorized: 0, unusual: 0, overdue_cards: 0 }
  const totalAlerts = c.large + c.unusual + c.pending + c.overdue_cards

  const sections: { key: Section; label: string; count: number; icon: React.ReactNode; activeBorder: string; activeBg: string; activeText: string }[] = [
    { key: 'large', label: 'Large Charges', count: c.large, icon: <AlertTriangle className="h-4 w-4" />, activeBorder: 'border-rose-500/50', activeBg: 'bg-rose-500/10', activeText: 'text-rose-400' },
    { key: 'unusual', label: 'Unusual Amounts', count: c.unusual, icon: <TrendingUp className="h-4 w-4" />, activeBorder: 'border-amber-500/50', activeBg: 'bg-amber-500/10', activeText: 'text-amber-400' },
    { key: 'pending', label: 'Pending', count: c.pending, icon: <Clock className="h-4 w-4" />, activeBorder: 'border-indigo-500/50', activeBg: 'bg-indigo-500/10', activeText: 'text-indigo-400' },
    { key: 'overdue', label: 'Overdue Cards', count: c.overdue_cards, icon: <CreditCard className="h-4 w-4" />, activeBorder: 'border-rose-500/50', activeBg: 'bg-rose-500/10', activeText: 'text-rose-400' },
    { key: 'uncategorized', label: 'Uncategorized', count: c.uncategorized, icon: <HelpCircle className="h-4 w-4" />, activeBorder: 'border-slate-500/50', activeBg: 'bg-slate-500/10', activeText: 'text-slate-400' },
  ]

  const activeTxns = active === 'large' ? (data?.large ?? []) :
    active === 'unusual' ? (data?.unusual ?? []) :
    active === 'pending' ? (data?.pending ?? []) :
    active === 'uncategorized' ? (data?.uncategorized ?? []) : []

  const sorted = sortTxns(activeTxns)

  const renderTxnTable = (txns: Txn[]) => (
    txns.length === 0 ? (
      <p className="py-8 text-center text-sm text-slate-500">Nothing here — all clear!</p>
    ) : (
      <>
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-slate-500 border-b border-slate-700/50 bg-slate-900/50">
              <th className="px-4 py-2.5 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleSort('date')}>
                <span className="inline-flex items-center gap-1">Date <SortIcon col="date" /></span>
              </th>
              <th className="px-4 py-2.5 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleSort('vendor')}>
                <span className="inline-flex items-center gap-1">Merchant <SortIcon col="vendor" /></span>
              </th>
              <th className="px-4 py-2.5 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleSort('category')}>
                <span className="inline-flex items-center gap-1">Category <SortIcon col="category" /></span>
              </th>
              <th className="px-4 py-2.5 text-left font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleSort('account')}>
                <span className="inline-flex items-center gap-1">Account <SortIcon col="account" /></span>
              </th>
              <th className="px-4 py-2.5 text-left font-medium">Details</th>
              <th className="px-4 py-2.5 text-right font-medium cursor-pointer select-none hover:text-slate-300 transition-colors" onClick={() => toggleSort('amount')}>
                <span className="inline-flex items-center gap-1 justify-end">Amount <SortIcon col="amount" /></span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/20">
            {txns.map((t, i) => (
              <tr key={t.id || i} className="text-slate-400 hover:bg-slate-700/20 transition-colors">
                <td className="whitespace-nowrap px-4 py-2.5">{fmtD(t.date)}</td>
                <td className="px-4 py-2.5">
                  <p className="font-medium text-slate-200 truncate max-w-[200px]">{t.vendor}</p>
                  <p className="truncate text-[10px] text-slate-600 max-w-[200px]">{t.description}</p>
                </td>
                <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{t.category}</td>
                <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{t.account}</td>
                <td className="px-4 py-2.5">
                  {t.flags && <span className="text-amber-400">{t.flags}</span>}
                  {t.pending && <span className="text-indigo-400">{t.flags ? ' · ' : ''}Pending</span>}
                </td>
                <td className={`whitespace-nowrap px-4 py-2.5 text-right font-bold tabular-nums ${t.amount < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                  {t.amount < 0 ? '-' : '+'}{fmt(Math.abs(t.amount))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-white">Monitor</h2>
        {totalAlerts > 0 && (
          <span className="rounded-full bg-rose-500/20 px-3 py-1 text-sm font-medium text-rose-400">
            {totalAlerts} alert{totalAlerts !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-400">{error}</div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {sections.map(s => (
          <button key={s.key} onClick={() => setActive(s.key)}
            className={`rounded-xl border p-3 text-left transition-all ${
              active === s.key
                ? `${s.activeBorder} ${s.activeBg}`
                : 'border-slate-700/50 bg-slate-800/80 hover:border-slate-600'
            }`}>
            <div className={`flex items-center gap-1.5 text-xs ${active === s.key ? s.activeText : 'text-slate-400'}`}>
              {s.icon} {s.label}
            </div>
            <p className={`mt-1 text-xl font-bold ${s.count > 0 ? (active === s.key ? s.activeText : 'text-white') : 'text-slate-600'}`}>
              {s.count}
            </p>
          </button>
        ))}
      </div>

      {/* Active Section Content */}
      <div className="rounded-xl border border-slate-700/50 bg-slate-800/80 overflow-hidden">
        <div className="border-b border-slate-700/50 px-4 py-3">
          <h3 className="font-semibold text-slate-200">
            {sections.find(s => s.key === active)?.label}
            <span className="ml-2 text-sm font-normal text-slate-500">
              ({sections.find(s => s.key === active)?.count ?? 0})
            </span>
          </h3>
        </div>

        {active === 'overdue' ? (
          (data?.overdue_cards ?? []).length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No overdue cards — all clear!</p>
          ) : (
            <table className="min-w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-700/50 bg-slate-900/50">
                  <th className="px-4 py-2.5 text-left font-medium">Card</th>
                  <th className="px-4 py-2.5 text-left font-medium">Institution</th>
                  <th className="px-4 py-2.5 text-left font-medium">Due Date</th>
                  <th className="px-4 py-2.5 text-left font-medium">Days Overdue</th>
                  <th className="px-4 py-2.5 text-right font-medium">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/20">
                {(data?.overdue_cards ?? []).map((card, i) => (
                  <tr key={i} className="text-slate-400 hover:bg-slate-700/20 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-slate-200">{card.name}</td>
                    <td className="px-4 py-2.5 text-slate-500">{card.institution}</td>
                    <td className="px-4 py-2.5 whitespace-nowrap">{fmtD(card.due_date)}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-rose-400">{card.days_overdue} day{card.days_overdue !== 1 ? 's' : ''}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-bold text-rose-400 tabular-nums">{fmt(card.balance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          renderTxnTable(sorted)
        )}
      </div>
    </div>
  )
}
